import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Check, X, Clock, RefreshCw } from 'lucide-react';
import { gradeChangeRequestApi } from '../../lib/api';
import { toast } from 'sonner';

export const GradeChangeRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const data = await gradeChangeRequestApi.getAll();
      setRequests(data);
    } catch (err) {
      toast.error('Failed to load grade change requests');
    } finally { setLoading(false); }
  };

  const handleApprove = async (requestId) => {
    try {
      await gradeChangeRequestApi.approve(requestId);
      toast.success('Grade change approved');
      fetchRequests();
    } catch (err) {
      toast.error(err.message || 'Failed to approve');
    }
  };

  const handleReject = async (requestId) => {
    try {
      await gradeChangeRequestApi.reject(requestId, '');
      toast.success('Grade change rejected');
      fetchRequests();
    } catch (err) {
      toast.error(err.message || 'Failed to reject');
    }
  };

  const filtered = requests.filter(r => filter === 'all' || r.status === filter);
  const pendingCount = requests.filter(r => r.status === 'pending').length;

  if (loading) return <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="grade-change-requests">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Grade Change Requests</h2>
          <p className="text-muted-foreground text-sm">{pendingCount} pending request{pendingCount !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex gap-2">
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)} data-testid={`filter-${f}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}{f === 'pending' && pendingCount > 0 ? ` (${pendingCount})` : ''}
            </Button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <RefreshCw className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No {filter === 'all' ? '' : filter} requests</p>
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Student</th>
                <th className="text-left p-3 font-medium">Current</th>
                <th className="text-left p-3 font-medium">Requested</th>
                <th className="text-left p-3 font-medium">Reason</th>
                <th className="text-left p-3 font-medium">Date</th>
                <th className="text-left p-3 font-medium">Status</th>
                <th className="text-right p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(req => (
                <tr key={req.request_id} className="border-t border-border hover:bg-muted/30" data-testid={`grade-request-row-${req.request_id}`}>
                  <td className="p-3">
                    <div className="font-medium">{req.student_name}</div>
                    <div className="text-xs text-muted-foreground">{req.student_email}</div>
                  </td>
                  <td className="p-3">Class {req.current_grade}</td>
                  <td className="p-3 font-semibold text-primary">Class {req.requested_grade}</td>
                  <td className="p-3 max-w-[200px] truncate">{req.reason}</td>
                  <td className="p-3 text-muted-foreground">{new Date(req.created_at).toLocaleDateString()}</td>
                  <td className="p-3">
                    <Badge variant={req.status === 'pending' ? 'secondary' : req.status === 'approved' ? 'default' : 'destructive'}>
                      {req.status}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    {req.status === 'pending' && (
                      <div className="flex gap-1 justify-end">
                        <Button size="sm" variant="default" onClick={() => handleApprove(req.request_id)} data-testid={`approve-${req.request_id}`}>
                          <Check className="w-3 h-3 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleReject(req.request_id)} data-testid={`reject-${req.request_id}`}>
                          <X className="w-3 h-3 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

